import * as React from "react";
import * as moment from "moment";
import {
    ALLIANZ_ESSENTIALS, ALLIANZ_SILVER,
    AnnualPolicyInfo,
    BreakdownCoverLevelEnum,
    CAR_WOLF_RENEWAL_NCD_PROOF,
    CommonHelpers,
    Document,
    ICustomerPortalService,
    INSTALMENT_STATUS_PENDING_PAYMENT,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_RENEWAL,
    InteractionHelper,
    Policy,
    POLICY_STATUS_MTC, POLICY_STATUS_PENDING_RENEWAL,
    PREMIUM_TYPE_ANNUAL,
    productHelper,
    Renewal,
    resourcesHelper,
    Subscription,
    Types
} from "@falcon/services";
import {resolveClaimType, resolveFuelType, resolveTransmission} from "../../../../../utils/resolveLookupValues";
import {
    formatDate,
    formatDateTime,
    getAddressLines,
    getCursorClassName,
    getIsOptionalExtraIncludedText,
    getLink,
    getCoverTierWording
} from "../../../../../CommonFunctions";
import {Link} from "react-router-dom";
import {lazyInject} from "@athena-portal/core";
import {DocumentListModal, getInteractionVehicleFuelType, PolicySummaryPriceWidget} from "@falcon/plugin-falcon-journey";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";

interface IProps {
    policy: Policy;
    // Policy that is still active but is going to be replaced
    currentlyActivePolicy?: Policy;
    subscription: Subscription;
    annualPolicyInfo?: AnnualPolicyInfo;
    vehicleIndex: number;
    isOldPolicy?: boolean;
    renewalProposal?: Renewal;
    renewalEnabled: boolean;
}

interface IState {
    documentPathToSizeMaps: Array<Map<string, string>>;
    instalmentsInfo?: InstalmentsInfo;
    showChangedStartDatePopup: boolean;
    ncdDocument: Document | null;
    mtaOnEndDateOfPolicy: boolean;
}

export class YourCarCoverPanel extends React.Component<IProps, IState> {

    private readonly policyToRender: Policy;
    private readonly vehicleToRender: any;
    private readonly cancelled: boolean;
    private readonly vehicleDrivers: any[] = [];
    private readonly mtaOnEndDateOfPolicy: boolean;

    private originalLatestQuoteDate: moment.Moment;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            documentPathToSizeMaps: [],
            showChangedStartDatePopup: false,
            ncdDocument: null,
            mtaOnEndDateOfPolicy: false
        };
        this.toggleHasAlreadyChangedStartDate = this.toggleHasAlreadyChangedStartDate.bind(this);

        this.policyToRender = this.props.currentlyActivePolicy || this.props.policy;

        this.vehicleToRender = this.policyToRender.interaction.vehicles[0];

        const originalQuoteDateRaw = this.policyToRender.interaction.renewalDetails?.periodStartDate ?? this.policyToRender.interaction.factors.originalQuoteDate;
        this.originalLatestQuoteDate = moment.utc(originalQuoteDateRaw, moment.ISO_8601);
        this.cancelled = this.props.policy.status === POLICY_STATUS_MTC;

        const allDrivers: any[] = [this.policyToRender.interaction.policyHolder].concat(this.policyToRender.interaction.additionalDrivers || []);
        if (this.vehicleToRender.driverIds) {
            this.vehicleDrivers = this.vehicleToRender.driverIds
                .map((driverId: string) => allDrivers.find((driver: any) => driverId === driver.personId))
                .filter((driver: any) => driver !== undefined);
        }

        this.mtaOnEndDateOfPolicy = moment.utc(this.props.policy.endDate).isSame(moment.utc(this.props.annualPolicyInfo?.maxFutureMtaDate), 'day');
    }

    public async componentDidMount() {
        const agreementVersionIds: number[] = this.props.policy.policyHistoryList
            .filter(ph => ph.status !== POLICY_STATUS_MTC)
            .map(ph => ph.id);

        await this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds)
            .subscribe((instalmentsInfo: any) => {
                this.setState({instalmentsInfo});
            });

        this.customerPortalService.getDocumentByNameAndPolicyNumber(this.policyToRender.policyNumber, CAR_WOLF_RENEWAL_NCD_PROOF + ".pdf").subscribe(
            (incomingNcd: Document) => {
                this.setState({ncdDocument: incomingNcd})
            });
    }

    public render() {
        const {vehicleIndex} = this.props;
        const interaction: any = this.policyToRender.interaction;
        const coverTier = this.policyToRender.interaction.coverTier;
        const premiumType: string = interaction.factors.premiumType;
        const price: number = premiumType === PREMIUM_TYPE_ANNUAL ? interaction.factors.annualPremium : this.getInstallmentPayment();
        const totalPaid: number = this.state.instalmentsInfo?.totalAmount ?? 0;

        return (

            <div className="section-panel your-cover-panel">
                <img className="main-icon policy-icon" src={`/${resourcesHelper.THEME}/img/policy-summary.png`}
                     alt="Car cover icon"/>
                <div className="section cover-details">
                    {!this.cancelled && <PolicySummaryPriceWidget
                        price={price}
                        premiumType={premiumType}
                        totalPaid={totalPaid}
                    />}
                    <h2 className="text-center mt-3 mb-3 policy-heading margin-top-0">Policy details</h2>
                    <div className="section-contents">
                        {this.renderCoverDetails(vehicleIndex)}
                    </div>
                </div>
                <div className="section car-details">
                    <h2 className="text-center mt-3 mb-3 car-details-heading margin-top-0">Car details</h2>
                    <img className="main-icon car-icon" src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                         alt="Car icon"/>
                    <div className="section-contents">
                        {this.renderCarDetails()}
                    </div>
                </div>
                <h2 className="text-center mt-3 mb-3 drivers-heading margin-top-0 margin-bottom-0">Driver(s)
                    details</h2>
                <img className="main-icon people-icon" src={`/${resourcesHelper.THEME}/img/account-details.png`}
                     alt="Car icon"/>
                {this.renderDriverDetailSections()}
                <div className="section policy-documents">
                    <div className="section-heading documents-heading margin-bottom-0">Policy documents</div>
                    <DocumentListModal
                        productName={productHelper.parseProductName(this.policyToRender.products[0])}
                        coverTier={coverTier}
                        policyDate={this.originalLatestQuoteDate.toDate()}
                        isBDIVisible={this.policyToRender.interaction.optionalExtras.breakdownCover}
                        isMLEVisible={this.policyToRender.interaction.optionalExtras.motorLegalExpenses}
                        isAccordion={false}
                    />
                </div>

                {!this.props.isOldPolicy && (
                    <div className="section button-section">
                        <div className="section-links">
                            {this.renderCancelPolicyButton(vehicleIndex)}
                        </div>
                        <div className="section-links">
                            <Link to={`/dashboard/pickAnnualMotorClaimKind/${this.props.policy.policyNumber}`}
                                  id={`makeClaim${vehicleIndex}`}
                                  className={`buttonLink`}>
                                <button type="button"
                                        className={`btn btn-primary btn-next plugin`}>
                                    Make a claim
                                </button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderCoverDetails(vehicleIndex: number): JSX.Element {
        const interaction: any = this.policyToRender.interaction;
        const premiumType: string = interaction.factors.premiumType;
        const closeBtn = <button className="btn-close" onClick={this.toggleHasAlreadyChangedStartDate}/>
        const process: string = interaction.process;
        const mtaNotPresent: boolean = process === INTERACTION_PROCESS_NB || process === INTERACTION_PROCESS_RENEWAL;
        const showChangeStartDateLink: boolean = this.shouldShowChangeStartDateLink();
        return (
            <React.Fragment>
                <div className="detail cover-start-date">
                    <div className="detail-key">Cover start date:</div>
                    <div className="detail-value"
                         id={`vehicle${vehicleIndex}-startDate`}>{this.renderPolicyStartDate(this.policyToRender)}</div>
                    {showChangeStartDateLink && (interaction.process === INTERACTION_PROCESS_NB || interaction.process === INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE)
                        && moment().utc().isBefore(moment(interaction.headerInfo.startDate).utc()) &&
                        <div className="detail-value">
                            <Link
                                className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                                to={getLink(`/journey/show/product/PropositionWolfMotorInsurance/process/mtaNbChangeStartDate/presetPage/${this.props.policy.interaction._id}`, interaction.hasChangedStartDate)}
                                onClick={this.toggleHasAlreadyChangedStartDate} id="changeStartDate">
                                Change start date
                            </Link>
                        </div>}
                </div>
                {interaction.hasChangedStartDate &&
                    <Modal className="falcon-modal" isOpen={this.state.showChangedStartDatePopup}
                           toggle={this.toggleHasAlreadyChangedStartDate}>
                        <ModalHeader toggle={this.toggleHasAlreadyChangedStartDate} close={closeBtn}/>
                        <ModalBody className="pt-0 overflow-auto">
                            <img src={`/${resourcesHelper.THEME}/img/alert-blue.svg`} alt="Blue alert icon"/>
                            <p className="d-inline mt-0 mb-2 ml-3 mr-3 font-weight-bold fs-24">Change of the policy
                                start date</p>
                            <p className="pt-3 mb-1">You have already changed your policy start date. Changing the start
                                date is only allowed once.
                                If the start date is still wrong, you'll need to cancel this policy and set up a new
                                one.</p>
                        </ModalBody>
                    </Modal>}
                <div className="detail">
                    <div className="detail-key">Cover expiry date:</div>
                    <div className="detail-value"
                         id={`vehicle${vehicleIndex}-endDate`}>{this.props.isOldPolicy ? `${formatDateTime(this.policyToRender.endDate)}`
                        : `${moment.utc(this.policyToRender.endDate, moment.ISO_8601).format("DD MMM YYYY")}, 23:59`}
                    </div>
                </div>

                {this.getDetail("Cover type:", "Comprehensive")}
                {this.getDetail("Cover level:", getCoverTierWording(interaction.coverTier))}
                {InteractionHelper.isPolicyQuotedBeforeFamilyCoverRemoval(interaction) &&
                    this.getDetail("Family Package:", interaction.optionalExtras.familyPackage ? "Included" : "Not included")}
                {this.getDetail("Breakdown cover:", this.getBreakdownWording(interaction.optionalExtras))}
                {this.getDetail("Protected no claim discount:", interaction.policyHolder.ncd.doNotDisclose ? "Included" : "Not included")}
                {this.getDetail("Motor legal expenses:", getIsOptionalExtraIncludedText(interaction.coverTier, interaction.optionalExtras.motorLegalExpenses))}
                {this.getDetail(this.getGHCIncludeDescription(interaction.coverTier), this.getGHCIncludeWording(interaction.coverTier, interaction.optionalExtras))}

                <div className="detail premium-tax">
                    <div className="detail-value">
                        {premiumType === PREMIUM_TYPE_ANNUAL && mtaNotPresent ? '* includes any changes made during the policy year' : '* includes any changes that you’ve made'}
                    </div>
                    <div
                        className="detail-value">{premiumType === PREMIUM_TYPE_ANNUAL ? 'incl. Insurance Premium Tax' : 'incl. Interest and Insurance Premium Tax'}</div>
                </div>
            </React.Fragment>
        );
    }

    private shouldShowChangeStartDateLink(): boolean {
        return process.env.REACT_APP_CHANGE_START_DATE_ENABLED ? JSON.parse(process.env.REACT_APP_CHANGE_START_DATE_ENABLED) : false;
    }

    private renderCarDetails(): JSX.Element {
        const {allAnnualMotorPoliciesAreCancelled} = this.props.subscription;
        const vehicleExcesses: any = this.policyToRender.interaction.vehicles[0].annualVehicleExcess;
        const vehicleFuelTypeActual = getInteractionVehicleFuelType(this.vehicleToRender.fuelType, this.vehicleToRender.fuelTypeFull);
        const linkClass = `text-decoration-underline ${getCursorClassName(allAnnualMotorPoliciesAreCancelled || this.hasDebt())} text-blue-1 muli-bold-16`;
        return (
            <React.Fragment>
                <div className="detail car-details-section">
                    <div
                        className="detail-value car-model">{`${this.vehicleToRender.make}, ${this.vehicleToRender.model}`}
                        <span className="number-plate-preview">({this.vehicleToRender.registrationNumber})</span></div>
                    <div
                        className="detail-value">{`${this.vehicleToRender.year}, ${resolveTransmission(this.vehicleToRender.transmission)}, ${resolveFuelType(vehicleFuelTypeActual)}`}</div>
                    <div className="text-center">
                        {!this.props.isOldPolicy && !this.isMtaDisabled() &&
                            <div className="detail-key link-section">
                                <Link id={`editCar1Details`} aria-label="Edit car details"
                                      to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${this.props.policy.interaction._id}`, this.hasDebt())}
                                      className={`edit-link ${linkClass}`}>Edit</Link>
                                <Link id={`replaceCar1`} aria-label="Replace car"
                                      to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaReplaceCar/preset/${this.props.policy.interaction._id}`, this.hasDebt())}
                                      className={`replace-link ${linkClass}`}>Replace car</Link>
                            </div>
                        }
                        {this.isMtaDisabled() &&
                            <>
                                <button type="button" disabled className="btn btn-gray" style={{ marginRight: "16px" }}>Edit</button>
                                <button type="button" disabled className="btn btn-gray">Replace car</button>
                            </>
                        }
                    </div>
                </div>
                <div className="detail excess-section">
                    <h2 className="text-center mt-3 mb-3 excess-heading margin-bottom-0">Excesses that apply</h2>
                    <div className="detail accidental-damage">
                        <div className={"text-center detail-key"}>Accidental damage:</div>
                        <div
                            className="detail-value price">{`£${vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess} (this includes £${vehicleExcesses.voluntaryExcess} voluntary excess)`}</div>
                        <br/>
                    </div>
                    <div className="detail fire-or-theft">
                        <div className={"text-center detail-key"}>Fire or theft:</div>
                        <div
                            className="detail-value price">{`£${vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess} (this includes £${vehicleExcesses.voluntaryExcess} voluntary excess)`}</div>
                        <br/>
                    </div>
                    <div className="detail mt-4">
                        <span>Your total excess applies every time you claim and to every car you’re claiming on.</span>
                    </div>
                    <div className="detail glass-repair mt-4">
                        <div className="font-weight-semi-bold">Glass-only excess costs you less</div>
                        <div className={"text-center detail-key"}>Glass repair excess:</div>
                        <div className="detail-value">just <span
                            className="price font-weight-semi-bold">{`£${vehicleExcesses.windscreenRepairExcess}`}</span>
                        </div>
                    </div>
                    <div className="detail glass-replacement mt-0">
                        <div className={"detail-key"}>Glass replacement excess:</div>
                        <div className="detail-value">just <span
                            className="price font-weight-semi-bold">{`£${vehicleExcesses.windscreenReplacementExcess}`}</span>
                        </div>
                    </div>
                    <div className={"detail non-recommended-excess mt-4 mb-0"}>
                        <div className="font-weight-semi-bold">Non-recommended repairer excess costs you more</div>
                        <div className="detail-key">Non-recommended repairer excess:</div>
                        <div className="detail-value">
                                <span
                                    className="price font-weight-bold">{`£${vehicleExcesses.nonRecommendedRepairerExcess}`}</span>
                        </div>
                    </div>
                    <div className={"detail mt-0 mb-0"}>This is in addition to the excesses shown above if you choose a
                        garage <span
                            className="font-weight-semi-bold">not</span> on our list of recommended repairers.
                    </div>
                    <div className={"detail mt-0"}>This also applies to glass replacement but <span
                        className="font-weight-semi-bold">not</span> glass-only repairs.
                    </div>
                </div>
                <div className="detail drivers">
                    <div className="detail-key driver-heading">Drivers</div>
                    {this.renderVehicleDrivers()}
                </div>
            </React.Fragment>
        );
    }

    private toggleHasAlreadyChangedStartDate(): void {
        this.setState((state: IState) => ({
            showChangedStartDatePopup: !state.showChangedStartDatePopup
        }));
    }

    private hasDebt() {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange} = this.props.annualPolicyInfo!;
        return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange;
    }

    private renderVehicleDrivers(): JSX.Element[] {
        return this.vehicleDrivers.map((driver: any, index: number) => {
            return (
                <div key={index} className="detail-value">
                    {`${driver.firstName} ${driver.lastName}`} {driver.personId === this.vehicleToRender.mainDriverId &&
                    <span>(main driver)</span>}
                </div>
            );
        });
    }

    private renderDriverDetailSections(): JSX.Element[] {
        return this.vehicleDrivers.map((driver: any, index: number) => (
            <div key={index} className="section driver-details">
                <div className="section-contents">
                    {this.renderDriverDetails(driver, index)}
                </div>
            </div>
        ));
    }

    private isMtaDisabled(): boolean {
        return this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy
            || this.props.subscription?.allAnnualMotorPoliciesAreCancelled;
    }

    private renderDriverDetails(driver: any, index: number): JSX.Element {
        const isPolicyHolder: boolean = driver.personId === this.policyToRender.interaction.policyHolder.personId
        const {allAnnualMotorPoliciesAreCancelled} = this.props.subscription;

        const linkClass = `text-decoration-underline ${getCursorClassName(allAnnualMotorPoliciesAreCancelled || this.hasDebt())} text-blue-1 muli-bold-16`;
        return (
            <React.Fragment>
                <div className="detail driver-name">
                    <div className="detail-value">{`${driver.firstName} ${driver.lastName}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Date of birth:</div>
                    <div
                        className="detail-value">{moment(driver.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}</div>
                </div>
                {(driver.drivingLicenceHandy && driver.dlnLookupData?.dlnLookupCallSucess) && <div className="detail">
                    <div className="detail-key">Driving licence number:</div>
                    <div className="detail-value">{driver.licence.number}</div>
                </div>}
                <div className="detail">
                    <div className="detail-key">Claims in the last 5 years:</div>
                    {this.renderClaims(driver)}
                </div>
                <div className="detail">
                    <div className="detail-key">Criminal convictions:</div>
                    <div
                        className="detail-value">{CommonHelpers.booleanToText(driver.hasUnspentCriminalConvictions, "Yes", "None")}</div>
                </div>
                {this.renderMotoringConvictions(driver)}
                {isPolicyHolder && this.renderPolicyHolderDetails()}
                <div className="text-center detail detail-actions">
                    {!this.props.isOldPolicy && !this.isMtaDisabled() &&
                        <div className="detail-key link-section">
                            <Link id={`viewDiver${index}Details`} aria-label="View driver details"
                                  to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${isPolicyHolder ? "PolicyHolder" : index - 1}/${this.props.policy.interaction._id}`, this.hasDebt())}
                                  className={`view-link ${linkClass}`}>View</Link>
                            <Link id={`editDiver${index}`} aria-label="Edit driver details"
                                  to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${isPolicyHolder ? "PolicyHolder" : index - 1}/${this.props.policy.interaction._id}`, this.hasDebt())}
                                  className={`edit-link ${linkClass}`}>Edit
                                details</Link>
                        </div>}
                    {!this.props.isOldPolicy && this.isMtaDisabled() &&
                        <>
                            <button type="button" disabled className="btn btn-gray" style={{ marginRight: "16px" }}>View</button>
                            <button type="button" disabled className="btn btn-gray">Edit details</button>
                        </>
                    }
                </div>
            </React.Fragment>
        );
    }

    private renderPolicyHolderDetails(): JSX.Element {
        const party = this.policyToRender.policyHolder;
        const {policyHolder} = this.policyToRender.interaction;
        const addressComponents: string[] = getAddressLines(policyHolder.address);
        const addressComponentsToRender: JSX.Element[] = addressComponents.map(
            (component: string, index: number) => index === 0 ?
                <span key={index} className="detail-value">{component}</span> :
                <React.Fragment><span key={index} className="detail-value">, {component}</span></React.Fragment>
        );
        const hasUnpaidChargebackInvoice: boolean = this.props.annualPolicyInfo!.hasUnpaidChargebackInvoice;

        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-key">No claim discount (NCD):</div>
                    <div className="detail-value">{policyHolder.ncd.doNotDisclose ? "Protected" : "Not protected"}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Years of no claim discount (NCD):</div>
                    <div
                        className="detail-value">{policyHolder.ncd.yearCount > 0 ? policyHolder.ncd.yearCount : "None"}</div>
                </div>
                <div className="detail address">
                    <div className="detail-key">Address:</div>
                    {addressComponentsToRender}
                    <div className="text-center change-address-link">
                        {!this.props.isOldPolicy && (!this.props.annualPolicyInfo?.maxMtaDateReached && !this.mtaOnEndDateOfPolicy) && (
                            <div className="detail-key">
                                <Link
                                    to={getLink('/journey/show/product/PropositionWolfMotorInsurance/process/mtaChangeAddress/preset/' + this.policyToRender.interaction._id, hasUnpaidChargebackInvoice)}
                                    className={`text-decoration-underline cursor-pointer text-blue-1 muli-bold-16 ${getCursorClassName(hasUnpaidChargebackInvoice)}`}
                                    id="editAddress"
                                >Change address</Link>
                            </div>
                        )}
                        {!this.props.isOldPolicy && (this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy) &&
                            <span className="detail-key change-address-link">Change address</span>}
                    </div>
                </div>
                <div className="detail">
                    <div className="detail-key">Email:</div>
                    <div className="detail-value">
                        {party.email}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private getBreakdownWording(optionalExtras: any):string {
        if (optionalExtras.breakdownCover === false) {
            return "Not included";
        }
        switch (optionalExtras.breakdownCoverLevel) {
            case BreakdownCoverLevelEnum.LOCAL:
                return "Included (Local)"
            case BreakdownCoverLevelEnum.UK:
                return "Included (National)"
            case BreakdownCoverLevelEnum.EU:
                return "Included (European)"
            default:
                throw new Error(`Unknown Breakdown Cover level: ${optionalExtras.breakdownCoverLevel}`);
        }
    }

    private renderPolicyStartDate(policy: any) {
        if (moment.utc(policy.startDate, moment.ISO_8601).format("HH:mm") === '00:00') {
            return `${moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY")}, ${this.props.isOldPolicy ? `00:00` : `00:01`}`;
        } else {
            return moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY, HH:mm");
        }
    }

    private renderClaims(driver: any): JSX.Element | JSX.Element[] {
        if (driver.hasClaims && driver.numberOfClaims > 0 && driver.claims?.length > 0) {
            return driver.claims.map((claim: any, index: any) =>
                <div key={index}
                     className="detail-value">{resolveClaimType(claim.claimType)}, {formatDate(claim.date, "MMM YYYY")}</div>
            )
        } else {
            return <div className="detail-value">None</div>
        }
    }

    private renderMotoringConvictions(driver: any): React.ReactNode {
        if (driver?.dlnLookupData?.dlnLookupCallSucess) {
            return (
                <div className="detail">
                    <div className="detail-key">
                        Motoring convictions:
                    </div>
                    <div className="detail-value">
                        As per your DVLA records
                    </div>
                </div>
            );
        } else {
            return (
                <div className="detail">
                    <div className="detail-key">
                        Motoring convictions:
                    </div>
                    {this.renderConvictionDetails(driver)}
                </div>
            )
        }
    }

    private renderConvictionDetails(driver: any): JSX.Element[] | JSX.Element {
        if (driver.hasConvictions && driver.convictions?.length > 0) {
            return driver.convictions.map((conviction: any, index: any) => (
                <div key={index} className="detail-value">
                    {`${conviction.convictionCode}, ${formatDate(conviction.convictionDate, "MMM YYYY")}`}
                </div>
            ))
        } else {
            return <div className="detail-value">None</div>
        }
    }

    private renderCancelPolicyButton(index: number): React.ReactNode {
        const hasPendingRenewal: boolean = this.props.renewalProposal != null && this.props.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
        if (hasPendingRenewal) {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/dashboard/annualMotor/cancel/${this.policyToRender.policyNumber}`}
                    routeState={{
                        isRenewalCancellation: true,
                        renewalCancellationUrl: '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + this.props.renewalProposal!._id
                    }}
                    disabled={this.props.subscription ? this.props.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${this.policyToRender.interaction?._id}`}
                    disabled={this.props.subscription ? this.props.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private getInstallmentPayment() {
        const nextInstalment = this.state.instalmentsInfo?.instalments.find(instalment => instalment.status === INSTALMENT_STATUS_PENDING_PAYMENT);
        return nextInstalment ? nextInstalment.amount : undefined;
    }

    private getDetail(description: string, value: string | null): React.ReactElement {
        return (
            <div className="detail">
                <div className="detail-key">{description}</div>
                {value}
            </div>
        )
    }

    private getGHCIncludeWording(coverTier: string, optionalExtras: any): string {
        switch (coverTier){
            case ALLIANZ_SILVER: return optionalExtras.courtesyCarTotalLoss ? "Included" : "Not included";
            case ALLIANZ_ESSENTIALS: return optionalExtras.courtesyCar ? "Included" : "Not included";
            default: return "Included as standard";
        }
    }

    private getGHCIncludeDescription(coverTier: string): string {
        if (coverTier === ALLIANZ_SILVER) {
            return "Guaranteed hire car if your car’s stolen or written off:";
        } else {
            return "Guaranteed hire car if your car’s stolen, written off or being repaired:";
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/yourCarCover/YourCarCoverPanel.tsx